import { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { agencyConfigApi } from '@/lib/api'
import { useTranslation } from 'react-i18next'
import { Settings, Users, Menu, ChevronDown, Shield, Receipt, LayoutDashboard, LogOut, Building2, Check, KeyRound, FolderKanban, Globe, Sun, Moon, Palette, MessageSquare, Loader2, HelpCircle, Clock, FileText, Coins, Activity, Calendar, Plus, Book } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { AgencyThemeProvider, useAgencyTheme } from '@/contexts/AgencyThemeContext'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext'
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext'
import { PermissionProvider, usePermissions, type ScreenCode } from '@/contexts/PermissionContext'
import { NotificationProvider } from '@/contexts/NotificationContext'
import { Chatbot } from '@/components/Chatbot'
import { NotificationBell } from '@/components/NotificationBell'
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog'
import { GlobalSearch } from '@/components/GlobalSearch'
import '@/i18n'
import LoginPage from './pages/LoginPage'
import SignUpPage from './pages/SignUpPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import SetPasswordPage from './pages/SetPasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import DashboardPage from './pages/DashboardPage'
import CustomFieldsPage from './pages/CustomFieldsPage'
import ClientsPage from './pages/ClientsPage'
import CreateClientPage from './pages/CreateClientPage'
import BusinessListPage from './pages/BusinessListPage'
import BusinessFormPage from './pages/BusinessFormPage'
import InvoicesPage from './pages/InvoicesPage'
import CreateInvoicePage from './pages/CreateInvoicePage'
import RecurringInvoicesPage from './pages/RecurringInvoicesPage'
import CreateRecurringInvoicePage from './pages/CreateRecurringInvoicePage'
import InvoiceTemplatesPage from './pages/InvoiceTemplatesPage'
import InvoiceTemplateFormPage from './pages/InvoiceTemplateFormPage'
import InvoiceSettingsPage from './pages/InvoiceSettingsPage'
import PaymentGatewaySettingsPage from './pages/PaymentGatewaySettingsPage'
import PaymentGatewayFormPage from './pages/PaymentGatewayFormPage'
import AgencyConfigurationPage from './pages/AgencyConfigurationPage'
import TaxMasterPage from './pages/TaxMasterPage'
import FeesMasterPage from './pages/FeesMasterPage'
import EmailTemplatesPage from './pages/EmailTemplatesPage'
import CategoryMasterPage from './pages/CategoryMasterPage'
import ProductMasterPage from './pages/ProductMasterPage'
import PaymentMethodMasterPage from './pages/PaymentMethodMasterPage'
import ExchangeRatesPage from './pages/ExchangeRatesPage'
import RolesPage from './pages/RolesPage'
import CreateRolePage from './pages/CreateRolePage'
import UsersPage from './pages/UsersPage'
import QuotesPage from './pages/QuotesPage'
import CreateQuotePage from './pages/CreateQuotePage'
import ExpensesPage from './pages/ExpensesPage'
import ExpenseFormPage from './pages/ExpenseFormPage'
import ProjectsPage from './pages/ProjectsPage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import BoardPage from './pages/BoardPage'
import AgenciesPage from './pages/AgenciesPage'
import CountriesPage from './pages/CountriesPage'
import CurrenciesPage from './pages/CurrenciesPage'
import SupportTicketsPage from './pages/SupportTicketsPage'
import UserGuidePage from './pages/UserGuidePage'
import AdminSupportTicketsPage from './pages/AdminSupportTicketsPage'
import SchedulerPage from './pages/SchedulerPage'
import HealthCheckPage from './pages/HealthCheckPage'
import LogViewerPage from './pages/LogViewerPage'
import AuditLogsPage from './pages/AuditLogsPage'
import TimeTrackingPage from './pages/TimeTrackingPage'
import TimeReportsPage from './pages/TimeReportsPage'
import OnboardingWizardPage from './pages/OnboardingWizardPage'
import LeadsPage from './pages/LeadsPage'
import LeadFormPage from './pages/LeadFormPage'
import LeadSettingsPage from './pages/LeadSettingsPage'
import PipelineSettingsPage from './pages/PipelineSettingsPage'
import DealsKanbanPage from './pages/DealsKanbanPage'
import DealFormPage from './pages/DealFormPage'
import DealDetailPage from './pages/DealDetailPage'
import AutomationsPage from './pages/AutomationsPage'
import AutomationFormPage from './pages/AutomationFormPage'
import AutomationLogsPage from './pages/AutomationLogsPage'
import CRMDashboardPage from './pages/CRMDashboardPage'
import CRMReportsPage from './pages/CRMReportsPage'
import ContractsPage from './pages/ContractsPage'
import ContractFormPage from './pages/ContractFormPage'
import ContractDetailPage from './pages/ContractDetailPage'
import ContractSendPage from './pages/ContractSendPage'
import ContractTemplatesPage from './pages/ContractTemplatesPage'
import ContractTemplateFormPage from './pages/ContractTemplateFormPage'
import PublicSigningPage from './pages/PublicSigningPage'
import PublicPaymentPage from './pages/PublicPaymentPage'
import PaymentHistoryPage from './pages/PaymentHistoryPage'
import ESigningSettingsPage from './pages/ESigningSettingsPage'
import SocialMediaCalendarsPage from './pages/SocialMediaCalendarsPage'
import SocialMediaCalendarDetailPage from './pages/SocialMediaCalendarDetailPage'
import BankAccountsPage from './pages/BankAccountsPage'
import StatementsPage from './pages/StatementsPage'
import TransactionInboxPage from './pages/TransactionInboxPage'
import ReconciliationRulesPage from './pages/ReconciliationRulesPage'
import RuleSuggestionsPage from './pages/RuleSuggestionsPage'
import SubscriptionPage from './pages/SubscriptionPage'

// Navigation items with translation keys and screen codes for permission checking
// Top-level navigation items (Agency Overview only)
const navItems: { to: string; labelKey: string; icon: typeof LayoutDashboard; screenCode: ScreenCode }[] = [
  { to: '/', labelKey: 'nav.agencyOverview', icon: LayoutDashboard, screenCode: 'DASHBOARD' },
]

// Clients & Lead submenu items (includes Clients and CRM)
const clientsLeadItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/clients', labelKey: 'nav.clients', screenCode: 'CLIENTS' },
]

const crmItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/crm/dashboard', labelKey: 'nav.crmDashboard', screenCode: 'CRM_DASHBOARD' },
  { to: '/crm/leads', labelKey: 'nav.leads', screenCode: 'CRM_LEADS' },
  { to: '/crm/deals', labelKey: 'nav.deals', screenCode: 'CRM_DEALS' },
  { to: '/crm/pipelines', labelKey: 'nav.pipelines', screenCode: 'CRM_PIPELINES' },
  { to: '/crm/automations', labelKey: 'nav.automations', screenCode: 'CRM_AUTOMATIONS' },
  { to: '/crm/reports', labelKey: 'nav.crmReports', screenCode: 'CRM_REPORTS' },
  { to: '/crm/settings', labelKey: 'nav.crmSettings', screenCode: 'CRM_SETTINGS' },
]

// Finances submenu items (renamed from Billing)
const financesItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/billing/quotes', labelKey: 'nav.quotes', screenCode: 'QUOTES' },
  { to: '/billing/invoices', labelKey: 'nav.invoices', screenCode: 'INVOICES' },
  { to: '/billing/recurring-invoices', labelKey: 'nav.recurringInvoices', screenCode: 'RECURRING_INVOICES' },
  { to: '/billing/payments', labelKey: 'nav.paymentHistory', screenCode: 'PAYMENT_HISTORY' },
  { to: '/expenses', labelKey: 'nav.expenses', screenCode: 'EXPENSES' },
]

// Reconciliation submenu items
const reconciliationItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/reconciliation/inbox', labelKey: 'nav.transactionInbox', screenCode: 'RECONCILIATION' },
  { to: '/reconciliation/bank-accounts', labelKey: 'nav.bankAccounts', screenCode: 'RECONCILIATION' },
  { to: '/reconciliation/statements', labelKey: 'nav.statements', screenCode: 'RECONCILIATION' },
  { to: '/reconciliation/rules', labelKey: 'nav.reconciliationRules', screenCode: 'RECONCILIATION' },
  { to: '/reconciliation/rule-suggestions', labelKey: 'nav.aiSuggestions', screenCode: 'RECONCILIATION' },
]

// Delivery submenu items (Projects, Time Tracking)
const deliveryItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/projects', labelKey: 'nav.projects', screenCode: 'PROJECTS' },
  { to: '/time-tracking', labelKey: 'nav.timeTracking', screenCode: 'TIME_TRACKING' },
  { to: '/time-reports', labelKey: 'nav.timeReports', screenCode: 'TIME_REPORTS' },
]

const contractsItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/contracts', labelKey: 'nav.contracts', screenCode: 'CONTRACTS' },
  { to: '/contracts/templates', labelKey: 'nav.contractTemplates', screenCode: 'CONTRACT_TEMPLATES' },
]

const socialMediaItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/social-media/calendars', labelKey: 'nav.socialMediaCalendars', screenCode: 'SOCIAL_MEDIA_CALENDARS' },
]

// Admin submenu items (Masters, User Access, Settings)
const mastersItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/masters/tax', labelKey: 'nav.taxMaster', screenCode: 'TAX_MASTER' },
  { to: '/masters/fees', labelKey: 'nav.feesMaster', screenCode: 'FEES_MASTER' },
  { to: '/masters/categories', labelKey: 'nav.categoryMaster', screenCode: 'CATEGORY_MASTER' },
  { to: '/masters/products', labelKey: 'nav.productMaster', screenCode: 'PRODUCT_MASTER' },
  { to: '/masters/payment-methods', labelKey: 'nav.paymentMethods', screenCode: 'PAYMENT_METHODS' },
  { to: '/masters/exchange-rates', labelKey: 'nav.exchangeRates', screenCode: 'EXCHANGE_RATES' },
]

const uamItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/uam/roles', labelKey: 'nav.roles', screenCode: 'ROLES' },
  { to: '/uam/users', labelKey: 'nav.users', screenCode: 'USERS' },
  { to: '/settings/audit-logs', labelKey: 'nav.auditLogs', screenCode: 'AUDIT_LOGS' },
]

const settingsItems: { to: string; labelKey: string; screenCode: ScreenCode }[] = [
  { to: '/settings/agency', labelKey: 'nav.agencyConfiguration', screenCode: 'AGENCY_CONFIG' },
  { to: '/settings/custom-fields', labelKey: 'nav.customFields', screenCode: 'CUSTOM_FIELDS' },
  { to: '/settings/billing-templates', labelKey: 'nav.billingTemplates', screenCode: 'BILLING_TEMPLATES' },
  { to: '/settings/email-templates', labelKey: 'nav.emailTemplates', screenCode: 'EMAIL_TEMPLATES' },
  { to: '/settings/invoice', labelKey: 'nav.invoiceSettings', screenCode: 'INVOICE_SETTINGS' },
  { to: '/settings/payment-gateways', labelKey: 'nav.paymentGateways', screenCode: 'PAYMENT_GATEWAYS' },
  { to: '/settings/esigning', labelKey: 'nav.esigningSettings', screenCode: 'AGENCY_CONFIG' },
]

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { isSuperAdmin, user, tenants, currentTenant, switchTenant } = useAuth()
  const { canView } = usePermissions()
  // Main menu states
  const [clientsLeadOpen, setClientsLeadOpen] = useState(location.pathname.startsWith('/clients') || location.pathname.startsWith('/crm'))
  const [financesOpen, setFinancesOpen] = useState(location.pathname.startsWith('/billing') || location.pathname.startsWith('/expenses'))
  const [reconciliationOpen, setReconciliationOpen] = useState(location.pathname.startsWith('/reconciliation'))
  const [deliveryOpen, setDeliveryOpen] = useState(location.pathname.startsWith('/projects') || location.pathname.startsWith('/time-tracking') || location.pathname.startsWith('/time-reports'))
  const [contractsOpen, setContractsOpen] = useState(location.pathname.startsWith('/contracts'))
  const [adminOpen, setAdminOpen] = useState(location.pathname.startsWith('/masters') || location.pathname.startsWith('/uam') || location.pathname.startsWith('/settings'))
  const [isSwitching, setIsSwitching] = useState(false)
  // Sub-menu states for Clients & Lead (CRM only, Clients is direct link)
  const [crmSubOpen, setCrmSubOpen] = useState(location.pathname.startsWith('/crm'))
  // Sub-menu states for Admin
  const [mastersSubOpen, setMastersSubOpen] = useState(location.pathname.startsWith('/masters'))
  const [uamSubOpen, setUamSubOpen] = useState(location.pathname.startsWith('/uam'))
  const [settingsSubOpen, setSettingsSubOpen] = useState(location.pathname.startsWith('/settings'))

  // Helper to toggle a menu and close all others
  const toggleMenu = (menu: 'clientsLead' | 'finances' | 'reconciliation' | 'delivery' | 'contracts' | 'admin') => {
    if (collapsed) return
    setClientsLeadOpen(menu === 'clientsLead' ? !clientsLeadOpen : false)
    setFinancesOpen(menu === 'finances' ? !financesOpen : false)
    setReconciliationOpen(menu === 'reconciliation' ? !reconciliationOpen : false)
    setDeliveryOpen(menu === 'delivery' ? !deliveryOpen : false)
    setContractsOpen(menu === 'contracts' ? !contractsOpen : false)
    setAdminOpen(menu === 'admin' ? !adminOpen : false)
  }

  // Helper to toggle admin sub-menu and close all other admin sub-menus
  const toggleAdminSubMenu = (submenu: 'masters' | 'uam' | 'settings') => {
    setMastersSubOpen(submenu === 'masters' ? !mastersSubOpen : false)
    setUamSubOpen(submenu === 'uam' ? !uamSubOpen : false)
    setSettingsSubOpen(submenu === 'settings' ? !settingsSubOpen : false)
  }
  const isClientsLeadActive = location.pathname.startsWith('/clients') || location.pathname.startsWith('/crm')
  const isFinancesActive = location.pathname.startsWith('/billing') || location.pathname.startsWith('/expenses')
  const isReconciliationActive = location.pathname.startsWith('/reconciliation')
  const isDeliveryActive = location.pathname.startsWith('/projects') || location.pathname.startsWith('/time-tracking') || location.pathname.startsWith('/time-reports')
  const isContractsActive = location.pathname.startsWith('/contracts')
  const isSocialMediaActive = location.pathname.startsWith('/social-media')
  const isAdminActive = location.pathname.startsWith('/masters') || location.pathname.startsWith('/uam') || location.pathname.startsWith('/settings')
  const { hamburgerMenuColor, sidebarTextColor, accentColor, secondaryColor } = useAgencyTheme()

  // Filter navigation items based on permissions
  const filteredNavItems = navItems.filter(item => canView(item.screenCode))
  const filteredClientsLeadItems = clientsLeadItems.filter(item => canView(item.screenCode))
  const filteredCrmItems = crmItems.filter(item => canView(item.screenCode))
  const filteredFinancesItems = financesItems.filter(item => canView(item.screenCode))
  const filteredReconciliationItems = reconciliationItems.filter(item => canView(item.screenCode))
  const filteredDeliveryItems = deliveryItems.filter(item => canView(item.screenCode))
  const filteredContractsItems = contractsItems.filter(item => canView(item.screenCode))
  const filteredSocialMediaItems = socialMediaItems.filter(item => canView(item.screenCode))
  const filteredMastersItems = mastersItems.filter(item => canView(item.screenCode))
  const filteredUamItems = uamItems.filter(item => canView(item.screenCode))
  const filteredSettingsItems = settingsItems.filter(item => canView(item.screenCode))
  // Check if user has access to any admin items
  const hasAdminAccess = filteredMastersItems.length > 0 || filteredUamItems.length > 0 || filteredSettingsItems.length > 0
  // Check if user has access to any clients & lead items
  const hasClientsLeadAccess = filteredClientsLeadItems.length > 0 || filteredCrmItems.length > 0

  const hasMultipleTenants = tenants.length > 1

  const handleSwitchTenant = async (tenantId: string, tenantName: string) => {
    if (tenantId === user?.tenant_id) return
    setIsSwitching(true)
    toast.loading(`Switching to ${tenantName}...`, { id: 'tenant-switch' })
    try {
      await switchTenant(tenantId)
      toast.success(`Switched to ${tenantName}`, { id: 'tenant-switch' })
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (error) {
      console.error('Failed to switch tenant:', error)
      toast.error('Failed to switch agency. Please try again.', { id: 'tenant-switch' })
      setIsSwitching(false)
    }
  }

  // Get agency theme for logo
  const { logoUrl, agencyName } = useAgencyTheme()

  return (
    <div
      className={cn(
        'hidden md:flex flex-col h-full transition-all duration-300 backdrop-blur-xl',
        collapsed ? 'w-16' : 'w-52'
      )}
      style={{
        backgroundColor: `${hamburgerMenuColor}E8`, // 91% opacity for glass effect
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Agency Logo and Name - Clickable to toggle sidebar */}
      <button
        onClick={onToggle}
        className="flex items-center gap-3 h-16 px-3 border-b border-white/10 w-full hover:bg-white/5 transition-colors cursor-pointer"
        style={{ borderColor: `${sidebarTextColor}15` }}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={agencyName}
            className={cn(
              'h-10 w-10 rounded-xl object-contain flex-shrink-0',
              collapsed && 'mx-auto'
            )}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <div
            className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0',
              collapsed && 'mx-auto'
            )}
            style={{ backgroundColor: '#FF2F6D' }}
          >
            <LayoutDashboard className="h-5 w-5 text-white" />
          </div>
        )}
        {!collapsed && (
          <div className="flex flex-col min-w-0 text-left">
            <span className="text-sm font-bold truncate" style={{ color: sidebarTextColor }}>
              {agencyName || 'AgencyOS'}
            </span>
            <span className="text-[10px] opacity-70" style={{ color: sidebarTextColor }}>
              Business Suite
            </span>
          </div>
        )}
      </button>

      {/* Agency Selector - only show when not collapsed and user has multiple agencies */}
      {!collapsed && hasMultipleTenants && (
        <div className="flex items-center justify-end h-10 px-2" style={{ borderColor: `${sidebarTextColor}15` }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="max-w-[100px] justify-between hover:bg-white/10"
                style={{ color: sidebarTextColor }}
                disabled={isSwitching}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {isSwitching ? (
                    <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                  ) : (
                    <Building2 className="h-3 w-3 flex-shrink-0" />
                  )}
                  <span className="truncate text-[10px]">{currentTenant?.tenant_name || 'Select'}</span>
                </div>
                <ChevronDown className="h-3 w-3 flex-shrink-0 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Switch Agency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {tenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.tenant_id}
                  onClick={() => handleSwitchTenant(tenant.tenant_id, tenant.tenant_name)}
                  className="flex items-center justify-between"
                  disabled={isSwitching}
                >
                  <div className="flex flex-col">
                    <span>{tenant.tenant_name}</span>
                    <span className="text-xs text-muted-foreground">{tenant.role_name}</span>
                  </div>
                  {tenant.tenant_id === user?.tenant_id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {/* Super Admin Only - Show Agencies and Support Tickets */}
        {isSuperAdmin ? (
          <>
            {/* Super Admin Badge */}
            {!collapsed && (
              <div className="flex items-center gap-2 px-3 py-2 mb-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold" style={{ color: sidebarTextColor }}>{t('nav.superAdmin')}</span>
                  <span className="text-[10px] opacity-70" style={{ color: sidebarTextColor }}>{t('nav.platformAdmin')}</span>
                </div>
              </div>
            )}
            {collapsed && (
              <div className="flex justify-center py-2 mb-2" title={t('nav.superAdmin')}>
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-white" />
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-b border-white/10 mb-2" style={{ borderColor: `${sidebarTextColor}15` }} />

            <Link
              to="/super-admin/agencies"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/agencies' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/agencies' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/agencies' ? secondaryColor : undefined
              }}
              title={collapsed ? t('nav.agencies') : undefined}
            >
              <Building2 className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{t('nav.agencies')}</span>}
            </Link>
            <Link
              to="/super-admin/countries"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/countries' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/countries' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/countries' ? secondaryColor : undefined
              }}
              title={collapsed ? t('nav.countries') : undefined}
            >
              <Globe className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{t('nav.countries')}</span>}
            </Link>
            <Link
              to="/super-admin/currencies"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/currencies' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/currencies' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/currencies' ? secondaryColor : undefined
              }}
              title={collapsed ? t('nav.currencies') : undefined}
            >
              <Coins className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{t('nav.currencies')}</span>}
            </Link>
            <Link
              to="/super-admin/support"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/support' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/support' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/support' ? secondaryColor : undefined
              }}
              title={collapsed ? t('nav.supportTickets') : undefined}
            >
              <MessageSquare className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{t('nav.supportTickets')}</span>}
            </Link>
            <Link
              to="/super-admin/scheduler"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/scheduler' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/scheduler' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/scheduler' ? secondaryColor : undefined
              }}
              title={collapsed ? 'Scheduler' : undefined}
            >
              <Clock className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>Scheduler</span>}
            </Link>
            <Link
              to="/super-admin/health"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/health' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/health' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/health' ? secondaryColor : undefined
              }}
              title={collapsed ? 'Health Check' : undefined}
            >
              <Activity className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>Health Check</span>}
            </Link>
            <Link
              to="/super-admin/logs"
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                location.pathname !== '/super-admin/logs' && 'hover:bg-white/10',
                collapsed && 'justify-center px-2'
              )}
              style={{
                color: location.pathname === '/super-admin/logs' ? accentColor : sidebarTextColor,
                backgroundColor: location.pathname === '/super-admin/logs' ? secondaryColor : undefined
              }}
              title={collapsed ? 'Log Viewer' : undefined}
            >
              <FileText className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>Log Viewer</span>}
            </Link>
          </>
        ) : (
          <>
            {/* Regular User Navigation */}
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
                    collapsed && 'justify-center px-2'
                  )}
                  style={{
                    color: isActive ? accentColor : sidebarTextColor
                  }}
                  title={collapsed ? t(item.labelKey) : undefined}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span>{t(item.labelKey)}</span>}
                </Link>
              )
            })}

            {/* Clients & Lead with submenu - Clients directly, CRM as sub-menu */}
        {hasClientsLeadAccess && (
        <div>
          <button
            onClick={() => toggleMenu('clientsLead')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isClientsLeadActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.clientsLead') : undefined}
          >
            <Users className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.clientsLead')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', clientsLeadOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && clientsLeadOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {/* Clients - direct link */}
              {filteredClientsLeadItems.map((item) => {
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                      isActive && 'font-medium'
                    )}
                    style={{
                      color: isActive ? accentColor : sidebarTextColor
                    }}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              })}
              {/* CRM sub-menu */}
              {filteredCrmItems.length > 0 && (
                <div>
                  <button
                    onClick={() => setCrmSubOpen(!crmSubOpen)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10"
                    style={{
                      color: location.pathname.startsWith('/crm') ? accentColor : sidebarTextColor
                    }}
                  >
                    <span className="flex-1 text-left">{t('nav.crm')}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', crmSubOpen && 'rotate-180')} />
                  </button>
                  {crmSubOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {filteredCrmItems.map((item) => {
                        const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={cn(
                              'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                              isActive && 'font-medium'
                            )}
                            style={{
                              color: isActive ? accentColor : sidebarTextColor
                            }}
                          >
                            {t(item.labelKey)}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Finances with submenu (renamed from Billing) */}
        {filteredFinancesItems.length > 0 && (
        <div>
          <button
            onClick={() => toggleMenu('finances')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isFinancesActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.finances') : undefined}
          >
            <Receipt className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.finances')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', financesOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && financesOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {filteredFinancesItems.map((item) => {
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                      isActive && 'font-medium'
                    )}
                    style={{
                      color: isActive ? accentColor : sidebarTextColor
                    }}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
        )}

        {/* Reconciliation with submenu */}
        {filteredReconciliationItems.length > 0 && (
        <div>
          <button
            onClick={() => toggleMenu('reconciliation')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isReconciliationActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.reconciliation') : undefined}
          >
            <Coins className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.reconciliation')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', reconciliationOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && reconciliationOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {filteredReconciliationItems.map((item) => {
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                      isActive && 'font-medium'
                    )}
                    style={{
                      color: isActive ? accentColor : sidebarTextColor
                    }}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
        )}

        {/* Delivery with submenu - Projects, Time Tracking */}
        {filteredDeliveryItems.length > 0 && (
        <div>
          <button
            onClick={() => toggleMenu('delivery')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isDeliveryActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.delivery') : undefined}
          >
            <FolderKanban className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.delivery')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', deliveryOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && deliveryOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {filteredDeliveryItems.map((item) => {
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                      isActive && 'font-medium'
                    )}
                    style={{
                      color: isActive ? accentColor : sidebarTextColor
                    }}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
        )}

        {/* Contracts with submenu - only show if user has access to any contracts item */}
        {filteredContractsItems.length > 0 && (
        <div>
          <button
            onClick={() => toggleMenu('contracts')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isContractsActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.contracts') : undefined}
          >
            <FileText className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.contracts')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', contractsOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && contractsOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {filteredContractsItems.map((item) => {
                // For /contracts, exclude /contracts/templates paths to avoid both items being highlighted
                const isActive = item.to === '/contracts'
                  ? (location.pathname === item.to || location.pathname.startsWith(item.to + '/')) && !location.pathname.startsWith('/contracts/templates')
                  : location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                      isActive && 'font-medium'
                    )}
                    style={{
                      color: isActive ? accentColor : sidebarTextColor
                    }}
                  >
                    {t(item.labelKey)}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
        )}

        {/* Social Media Calendar - only show if user has access */}
        {filteredSocialMediaItems.length > 0 && (
          <Link
            to="/social-media/calendars"
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isSocialMediaActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.socialMediaCalendars') : undefined}
          >
            <Calendar className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>{t('nav.socialMediaCalendars')}</span>}
          </Link>
        )}

        {/* Admin with submenu - Masters, User Access, Settings as sub-menus */}
        {hasAdminAccess && (
        <div>
          <button
            onClick={() => toggleMenu('admin')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10',
              collapsed && 'justify-center px-2'
            )}
            style={{
              color: isAdminActive ? accentColor : sidebarTextColor
            }}
            title={collapsed ? t('nav.admin') : undefined}
          >
            <Settings className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{t('nav.admin')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', adminOpen && 'rotate-180')} />
              </>
            )}
          </button>
          {!collapsed && adminOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {/* Masters sub-menu */}
              {filteredMastersItems.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleAdminSubMenu('masters')}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10"
                    style={{
                      color: location.pathname.startsWith('/masters') ? accentColor : sidebarTextColor
                    }}
                  >
                    <span className="flex-1 text-left">{t('nav.masters')}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', mastersSubOpen && 'rotate-180')} />
                  </button>
                  {mastersSubOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {filteredMastersItems.map((item) => {
                        const isActive = location.pathname === item.to
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={cn(
                              'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                              isActive && 'font-medium'
                            )}
                            style={{
                              color: isActive ? accentColor : sidebarTextColor
                            }}
                          >
                            {t(item.labelKey)}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* User Access sub-menu */}
              {filteredUamItems.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleAdminSubMenu('uam')}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10"
                    style={{
                      color: location.pathname.startsWith('/uam') ? accentColor : sidebarTextColor
                    }}
                  >
                    <span className="flex-1 text-left">{t('nav.userAccess')}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', uamSubOpen && 'rotate-180')} />
                  </button>
                  {uamSubOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {filteredUamItems.map((item) => {
                        const isActive = location.pathname === item.to
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={cn(
                              'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                              isActive && 'font-medium'
                            )}
                            style={{
                              color: isActive ? accentColor : sidebarTextColor
                            }}
                          >
                            {t(item.labelKey)}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* Settings sub-menu */}
              {filteredSettingsItems.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleAdminSubMenu('settings')}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/10"
                    style={{
                      color: location.pathname.startsWith('/settings') ? accentColor : sidebarTextColor
                    }}
                  >
                    <span className="flex-1 text-left">{t('nav.settings')}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform', settingsSubOpen && 'rotate-180')} />
                  </button>
                  {settingsSubOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {filteredSettingsItems.map((item) => {
                        const isActive = location.pathname === item.to
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={cn(
                              'block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/10',
                              isActive && 'font-medium'
                            )}
                            style={{
                              color: isActive ? accentColor : sidebarTextColor
                            }}
                          >
                            {t(item.labelKey)}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}

          </>
        )}
      </nav>

      {/* Bottom Section - Plan Status Card and Create New Button */}
      {!isSuperAdmin && (
        <div className="p-3 space-y-3">
          {/* Plan Status Card */}
          {!collapsed && (
            <div
              className="rounded-xl p-3"
              style={{ backgroundColor: `${sidebarTextColor}15` }}
            >
              {currentTenant?.subscription_tier === 'PREMIUM' ? (
                <>
                  {/* Premium Plan - Show current status */}
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] font-semibold tracking-wider"
                      style={{ color: '#10B981' }}
                    >
                      ✓ PRO PLAN
                    </span>
                  </div>
                  <p
                    className="text-[11px] mt-1 opacity-80"
                    style={{ color: sidebarTextColor }}
                  >
                    You have access to all premium features.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-xs h-8 border-white/20 hover:bg-white/10"
                    style={{ color: hamburgerMenuColor, borderColor: hamburgerMenuColor }}
                    onClick={() => navigate('/subscription')}
                  >
                    Manage Plan
                  </Button>
                </>
              ) : (
                <>
                  {/* Free Trial or Lower Plan - Show upgrade option */}
                  <span
                    className="text-[10px] font-semibold tracking-wider"
                    style={{ color: '#FF2F6D' }}
                  >
                    {currentTenant?.subscription_tier === 'FREE_TRIAL' ? 'FREE TRIAL' : 'PRO PLAN'}
                  </span>
                  <p
                    className="text-[11px] mt-1 opacity-80"
                    style={{ color: sidebarTextColor }}
                  >
                    Upgrade for more seats and advanced automation tools.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-xs h-8 border-white/20 hover:bg-white/10"
                    style={{ color: hamburgerMenuColor, borderColor: hamburgerMenuColor }}
                    onClick={() => navigate('/subscription')}
                  >
                    Upgrade Now
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Create New Button with Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={cn(
                  'w-full font-semibold shadow-lg hover:opacity-90',
                  collapsed ? 'h-10 w-10 p-0' : 'h-11'
                )}
                style={{
                  backgroundColor: '#FF2F6D',
                  color: 'white',
                }}
              >
                <Plus className={cn('h-5 w-5', !collapsed && 'mr-2')} />
                {!collapsed && <span>{t('common.createNew')}</span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="w-48 mb-2">
              <DropdownMenuLabel>{t('common.createNew')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/clients?action=new" className="flex items-center cursor-pointer">
                  <Users className="mr-2 h-4 w-4" />
                  {t('clients.newClient')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/billing/invoices?action=new" className="flex items-center cursor-pointer">
                  <FileText className="mr-2 h-4 w-4" />
                  {t('invoices.newInvoice')}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}

function MobileNav() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Main menu states
  const [clientsLeadOpen, setClientsLeadOpen] = useState(false)
  const [financesOpen, setFinancesOpen] = useState(false)
  const [reconciliationOpen, setReconciliationOpen] = useState(false)
  const [deliveryOpen, setDeliveryOpen] = useState(false)
  const [contractsOpen, setContractsOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  // Sub-menu states for Clients & Lead (CRM only, Clients is direct link)
  const [crmSubOpen, setCrmSubOpen] = useState(false)
  // Sub-menu states for Admin
  const [mastersSubOpen, setMastersSubOpen] = useState(false)
  const [uamSubOpen, setUamSubOpen] = useState(false)
  const [settingsSubOpen, setSettingsSubOpen] = useState(false)
  const location = useLocation()

  // Helper to toggle a menu and close all others
  const toggleMenu = (menu: 'clientsLead' | 'finances' | 'reconciliation' | 'delivery' | 'contracts' | 'admin') => {
    setClientsLeadOpen(menu === 'clientsLead' ? !clientsLeadOpen : false)
    setFinancesOpen(menu === 'finances' ? !financesOpen : false)
    setReconciliationOpen(menu === 'reconciliation' ? !reconciliationOpen : false)
    setDeliveryOpen(menu === 'delivery' ? !deliveryOpen : false)
    setContractsOpen(menu === 'contracts' ? !contractsOpen : false)
    setAdminOpen(menu === 'admin' ? !adminOpen : false)
  }

  // Helper to toggle admin sub-menu and close all other admin sub-menus
  const toggleAdminSubMenu = (submenu: 'masters' | 'uam' | 'settings') => {
    setMastersSubOpen(submenu === 'masters' ? !mastersSubOpen : false)
    setUamSubOpen(submenu === 'uam' ? !uamSubOpen : false)
    setSettingsSubOpen(submenu === 'settings' ? !settingsSubOpen : false)
  }

  const isClientsLeadActive = location.pathname.startsWith('/clients') || location.pathname.startsWith('/crm')
  const isFinancesActive = location.pathname.startsWith('/billing') || location.pathname.startsWith('/expenses')
  const isReconciliationActive = location.pathname.startsWith('/reconciliation')
  const isDeliveryActive = location.pathname.startsWith('/projects') || location.pathname.startsWith('/time-tracking') || location.pathname.startsWith('/time-reports')
  const isContractsActive = location.pathname.startsWith('/contracts')
  const isSocialMediaActive = location.pathname.startsWith('/social-media')
  const isAdminActive = location.pathname.startsWith('/masters') || location.pathname.startsWith('/uam') || location.pathname.startsWith('/settings')
  const { agencyName, logoUrl, hamburgerMenuColor, sidebarTextColor, accentColor, secondaryColor, textColor } = useAgencyTheme()
  const { user, tenants, currentTenant, switchTenant, logout, isSuperAdmin } = useAuth()
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage()
  const { theme, setTheme } = useTheme()
  const { canView } = usePermissions()

  // Filter navigation items based on permissions
  const filteredNavItems = navItems.filter(item => canView(item.screenCode))
  const filteredClientsLeadItems = clientsLeadItems.filter(item => canView(item.screenCode))
  const filteredCrmItems = crmItems.filter(item => canView(item.screenCode))
  const filteredFinancesItems = financesItems.filter(item => canView(item.screenCode))
  const filteredReconciliationItems = reconciliationItems.filter(item => canView(item.screenCode))
  const filteredDeliveryItems = deliveryItems.filter(item => canView(item.screenCode))
  const filteredContractsItems = contractsItems.filter(item => canView(item.screenCode))
  const filteredSocialMediaItems = socialMediaItems.filter(item => canView(item.screenCode))
  const filteredMastersItems = mastersItems.filter(item => canView(item.screenCode))
  const filteredUamItems = uamItems.filter(item => canView(item.screenCode))
  const filteredSettingsItems = settingsItems.filter(item => canView(item.screenCode))
  // Check if user has access to any admin items
  const hasAdminAccess = filteredMastersItems.length > 0 || filteredUamItems.length > 0 || filteredSettingsItems.length > 0
  // Check if user has access to any clients & lead items
  const hasClientsLeadAccess = filteredClientsLeadItems.length > 0 || filteredCrmItems.length > 0

  const hasMultipleTenants = tenants.length > 1
  const currentLang = supportedLanguages.find(l => l.code === currentLanguage)

  const handleSwitchTenant = async (tenantId: string, tenantName: string) => {
    if (tenantId === user?.tenant_id) return
    setIsSwitching(true)
    toast.loading(`Switching to ${tenantName}...`, { id: 'tenant-switch' })
    try {
      await switchTenant(tenantId)
      toast.success(`Switched to ${tenantName}`, { id: 'tenant-switch' })
      // Small delay to ensure localStorage is written before reload
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (error) {
      console.error('Failed to switch tenant:', error)
      toast.error('Failed to switch agency. Please try again.', { id: 'tenant-switch' })
      setIsSwitching(false)
    }
  }

	  return (
	    <div
	      className="md:hidden flex items-center justify-between h-14 px-4 border-b sticky top-0 z-40 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border-white/20 dark:border-white/10"
	      style={{
	        borderColor: `${textColor}10`,
	        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
	      }}
	    >
      <Link to="/" className="flex items-center gap-2 text-xl font-bold" style={{ color: textColor }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt={agencyName}
            className="h-10 w-auto max-w-[120px] object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        )}
        <span className="truncate">{agencyName}</span>
      </Link>
      <div className="flex items-center gap-2">
        {/* Agency Selector in Header - only show if user has access to multiple agencies */}
        {hasMultipleTenants && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="max-w-[140px] justify-between"
                disabled={isSwitching}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {isSwitching ? (
                    <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
                  ) : (
                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                  )}
                  <span className="truncate text-xs">{currentTenant?.tenant_name || 'Select'}</span>
                </div>
                <ChevronDown className="h-3 w-3 flex-shrink-0 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Switch Agency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {tenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.tenant_id}
                  onClick={() => handleSwitchTenant(tenant.tenant_id, tenant.tenant_name)}
                  className="flex items-center justify-between"
                  disabled={isSwitching}
                >
                  <div className="flex flex-col">
                    <span>{tenant.tenant_name}</span>
                    <span className="text-xs text-muted-foreground">{tenant.role_name}</span>
                  </div>
                  {tenant.tenant_id === user?.tenant_id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="hover:bg-white/10" style={{ color: sidebarTextColor }}>
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-64 p-0 border-r-0 backdrop-blur-xl"
          style={{
            backgroundColor: `${hamburgerMenuColor}E8`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="flex items-center gap-2 h-14 px-4 border-b border-white/10" style={{ borderColor: `${sidebarTextColor}15` }}>
            {isSuperAdmin ? (
              // Super Admin - show shield icon
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Shield className="h-4 w-4 text-white" />
              </div>
            ) : (
              // Regular user - show agency logo
              logoUrl && (
                <img
                  src={logoUrl}
                  alt={agencyName}
                  className="h-10 w-auto max-w-[120px] object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              )
            )}
            <span className="text-xl font-bold truncate" style={{ color: sidebarTextColor }}>
              {isSuperAdmin ? t('nav.superAdminPortal') : agencyName}
            </span>
          </div>
          <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px - 120px)' }}>
            {/* Super Admin Only - Show Agencies, Countries, and Support */}
            {isSuperAdmin ? (
              <>
                {/* Super Admin Badge */}
                <div className="flex items-center gap-2 px-3 py-2 mb-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold" style={{ color: sidebarTextColor }}>{t('nav.superAdmin')}</span>
                    <span className="text-[10px] opacity-70" style={{ color: sidebarTextColor }}>{t('nav.platformAdmin')}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-b border-white/10 mb-2" style={{ borderColor: `${sidebarTextColor}15` }} />

                <Link
                  to="/super-admin/agencies"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/agencies' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/agencies' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/agencies' ? secondaryColor : undefined
                  }}
                >
                  <Building2 className="h-5 w-5" />
                  <span>{t('nav.agencies')}</span>
                </Link>
                <Link
                  to="/super-admin/countries"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/countries' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/countries' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/countries' ? secondaryColor : undefined
                  }}
                >
                  <Globe className="h-5 w-5" />
                  <span>{t('nav.countries')}</span>
                </Link>
                <Link
                  to="/super-admin/currencies"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/currencies' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/currencies' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/currencies' ? secondaryColor : undefined
                  }}
                >
                  <Coins className="h-5 w-5" />
                  <span>{t('nav.currencies')}</span>
                </Link>
                <Link
                  to="/super-admin/support"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/support' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/support' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/support' ? secondaryColor : undefined
                  }}
                >
                  <MessageSquare className="h-5 w-5" />
                  <span>{t('nav.supportTickets')}</span>
                </Link>
                <Link
                  to="/super-admin/scheduler"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/scheduler' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/scheduler' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/scheduler' ? secondaryColor : undefined
                  }}
                >
                  <Clock className="h-5 w-5" />
                  <span>Scheduler</span>
                </Link>
                <Link
                  to="/super-admin/health"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/health' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/health' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/health' ? secondaryColor : undefined
                  }}
                >
                  <Activity className="h-5 w-5" />
                  <span>Health Check</span>
                </Link>
                <Link
                  to="/super-admin/logs"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname !== '/super-admin/logs' && 'hover:bg-white/10'
                  )}
                  style={{
                    color: location.pathname === '/super-admin/logs' ? accentColor : sidebarTextColor,
                    backgroundColor: location.pathname === '/super-admin/logs' ? secondaryColor : undefined
                  }}
                >
                  <FileText className="h-5 w-5" />
                  <span>Log Viewer</span>
                </Link>
              </>
            ) : (
              <>
                {/* Regular User Navigation */}
                {filteredNavItems.map((item) => {
                  const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                  return (
	                    <Link
	                      key={item.to}
	                      to={item.to}
	                      onClick={() => setOpen(false)}
	                      className={cn(
	                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        !isActive && 'hover:bg-white/10'
                      )}
                      style={{
                        color: isActive ? accentColor : sidebarTextColor,
                        backgroundColor: isActive ? secondaryColor : undefined
                      }}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  )
                })}

                {/* Clients & Lead with submenu - Clients directly, CRM as sub-menu */}
            {hasClientsLeadAccess && (
            <div>
              <button
                onClick={() => toggleMenu('clientsLead')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isClientsLeadActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isClientsLeadActive ? accentColor : sidebarTextColor,
                  backgroundColor: isClientsLeadActive ? secondaryColor : undefined
                }}
              >
                <Users className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.clientsLead')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', clientsLeadOpen && 'rotate-180')} />
              </button>
              {clientsLeadOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  {/* Clients - direct link */}
                  {filteredClientsLeadItems.map((item) => {
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          'block px-3 py-2 rounded-lg text-sm transition-colors',
                          isActive ? 'font-medium' : 'hover:bg-white/10'
                        )}
                        style={{
                          color: isActive ? accentColor : sidebarTextColor,
                          backgroundColor: isActive ? secondaryColor : undefined
                        }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    )
                  })}
                  {/* CRM sub-menu */}
                  {filteredCrmItems.length > 0 && (
                    <div>
                      <button
                        onClick={() => setCrmSubOpen(!crmSubOpen)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          !location.pathname.startsWith('/crm') && 'hover:bg-white/10'
                        )}
                        style={{
                          color: location.pathname.startsWith('/crm') ? accentColor : sidebarTextColor,
                          backgroundColor: location.pathname.startsWith('/crm') ? secondaryColor : undefined
                        }}
                      >
                        <span className="flex-1 text-left">{t('nav.crm')}</span>
                        <ChevronDown className={cn('h-3 w-3 transition-transform', crmSubOpen && 'rotate-180')} />
                      </button>
                      {crmSubOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {filteredCrmItems.map((item) => {
                            const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                className={cn(
                                  'block px-3 py-2 rounded-lg text-sm transition-colors',
                                  isActive ? 'font-medium' : 'hover:bg-white/10'
                                )}
                                style={{
                                  color: isActive ? accentColor : sidebarTextColor,
                                  backgroundColor: isActive ? secondaryColor : undefined
                                }}
                              >
                                {t(item.labelKey)}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Finances with submenu (renamed from Billing) */}
            {filteredFinancesItems.length > 0 && (
            <div>
              <button
                onClick={() => toggleMenu('finances')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isFinancesActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isFinancesActive ? accentColor : sidebarTextColor,
                  backgroundColor: isFinancesActive ? secondaryColor : undefined
                }}
              >
                <Receipt className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.finances')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', financesOpen && 'rotate-180')} />
              </button>
              {financesOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {filteredFinancesItems.map((item) => {
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          'block px-3 py-2 rounded-lg text-sm transition-colors',
                          isActive ? 'font-medium' : 'hover:bg-white/10'
                        )}
                        style={{
                          color: isActive ? accentColor : sidebarTextColor,
                          backgroundColor: isActive ? secondaryColor : undefined
                        }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* Reconciliation with submenu */}
            {filteredReconciliationItems.length > 0 && (
            <div>
              <button
                onClick={() => toggleMenu('reconciliation')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isReconciliationActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isReconciliationActive ? accentColor : sidebarTextColor,
                  backgroundColor: isReconciliationActive ? secondaryColor : undefined
                }}
              >
                <Coins className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.reconciliation')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', reconciliationOpen && 'rotate-180')} />
              </button>
              {reconciliationOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {filteredReconciliationItems.map((item) => {
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          'block px-3 py-2 rounded-lg text-sm transition-colors',
                          isActive ? 'font-medium' : 'hover:bg-white/10'
                        )}
                        style={{
                          color: isActive ? accentColor : sidebarTextColor,
                          backgroundColor: isActive ? secondaryColor : undefined
                        }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* Delivery with submenu - Projects, Time Tracking */}
            {filteredDeliveryItems.length > 0 && (
            <div>
              <button
                onClick={() => toggleMenu('delivery')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isDeliveryActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isDeliveryActive ? accentColor : sidebarTextColor,
                  backgroundColor: isDeliveryActive ? secondaryColor : undefined
                }}
              >
                <FolderKanban className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.delivery')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', deliveryOpen && 'rotate-180')} />
              </button>
              {deliveryOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {filteredDeliveryItems.map((item) => {
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          'block px-3 py-2 rounded-lg text-sm transition-colors',
                          isActive ? 'font-medium' : 'hover:bg-white/10'
                        )}
                        style={{
                          color: isActive ? accentColor : sidebarTextColor,
                          backgroundColor: isActive ? secondaryColor : undefined
                        }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* Contracts with submenu - only show if user has access to any contracts item */}
            {filteredContractsItems.length > 0 && (
            <div>
              <button
                onClick={() => toggleMenu('contracts')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isContractsActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isContractsActive ? accentColor : sidebarTextColor,
                  backgroundColor: isContractsActive ? secondaryColor : undefined
                }}
              >
                <FileText className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.contracts')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', contractsOpen && 'rotate-180')} />
              </button>
              {contractsOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {filteredContractsItems.map((item) => {
                    // For /contracts, exclude /contracts/templates paths to avoid both items being highlighted
                    const isActive = item.to === '/contracts'
                      ? (location.pathname === item.to || location.pathname.startsWith(item.to + '/')) && !location.pathname.startsWith('/contracts/templates')
                      : location.pathname === item.to || location.pathname.startsWith(item.to + '/')
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className={cn(
                          'block px-3 py-2 rounded-lg text-sm transition-colors',
                          isActive ? 'font-medium' : 'hover:bg-white/10'
                        )}
                        style={{
                          color: isActive ? accentColor : sidebarTextColor,
                          backgroundColor: isActive ? secondaryColor : undefined
                        }}
                      >
                        {t(item.labelKey)}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
            )}

            {/* Social Media Calendar - only show if user has access */}
            {filteredSocialMediaItems.length > 0 && (
              <Link
                to="/social-media/calendars"
                onClick={() => setOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isSocialMediaActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isSocialMediaActive ? accentColor : sidebarTextColor,
                  backgroundColor: isSocialMediaActive ? secondaryColor : undefined
                }}
              >
                <Calendar className="h-5 w-5" />
                <span>{t('nav.socialMediaCalendars')}</span>
              </Link>
            )}

            {/* Admin with submenu - Masters, User Access, Settings */}
            {/* Admin with submenu - Masters, User Access, Settings as sub-menus */}
            {hasAdminAccess && (
            <div>
              <button
                onClick={() => toggleMenu('admin')}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  !isAdminActive && 'hover:bg-white/10'
                )}
                style={{
                  color: isAdminActive ? accentColor : sidebarTextColor,
                  backgroundColor: isAdminActive ? secondaryColor : undefined
                }}
              >
                <Settings className="h-5 w-5" />
                <span className="flex-1 text-left">{t('nav.admin')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', adminOpen && 'rotate-180')} />
              </button>
              {adminOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  {/* Masters sub-menu */}
                  {filteredMastersItems.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleAdminSubMenu('masters')}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          !location.pathname.startsWith('/masters') && 'hover:bg-white/10'
                        )}
                        style={{
                          color: location.pathname.startsWith('/masters') ? accentColor : sidebarTextColor,
                          backgroundColor: location.pathname.startsWith('/masters') ? secondaryColor : undefined
                        }}
                      >
                        <span className="flex-1 text-left">{t('nav.masters')}</span>
                        <ChevronDown className={cn('h-3 w-3 transition-transform', mastersSubOpen && 'rotate-180')} />
                      </button>
                      {mastersSubOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {filteredMastersItems.map((item) => {
                            const isActive = location.pathname === item.to
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                className={cn(
                                  'block px-3 py-2 rounded-lg text-sm transition-colors',
                                  isActive ? 'font-medium' : 'hover:bg-white/10'
                                )}
                                style={{
                                  color: isActive ? accentColor : sidebarTextColor,
                                  backgroundColor: isActive ? secondaryColor : undefined
                                }}
                              >
                                {t(item.labelKey)}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {/* User Access sub-menu */}
                  {filteredUamItems.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleAdminSubMenu('uam')}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          !location.pathname.startsWith('/uam') && 'hover:bg-white/10'
                        )}
                        style={{
                          color: location.pathname.startsWith('/uam') ? accentColor : sidebarTextColor,
                          backgroundColor: location.pathname.startsWith('/uam') ? secondaryColor : undefined
                        }}
                      >
                        <span className="flex-1 text-left">{t('nav.userAccess')}</span>
                        <ChevronDown className={cn('h-3 w-3 transition-transform', uamSubOpen && 'rotate-180')} />
                      </button>
                      {uamSubOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {filteredUamItems.map((item) => {
                            const isActive = location.pathname === item.to
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                className={cn(
                                  'block px-3 py-2 rounded-lg text-sm transition-colors',
                                  isActive ? 'font-medium' : 'hover:bg-white/10'
                                )}
                                style={{
                                  color: isActive ? accentColor : sidebarTextColor,
                                  backgroundColor: isActive ? secondaryColor : undefined
                                }}
                              >
                                {t(item.labelKey)}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Settings sub-menu */}
                  {filteredSettingsItems.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleAdminSubMenu('settings')}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          !location.pathname.startsWith('/settings') && 'hover:bg-white/10'
                        )}
                        style={{
                          color: location.pathname.startsWith('/settings') ? accentColor : sidebarTextColor,
                          backgroundColor: location.pathname.startsWith('/settings') ? secondaryColor : undefined
                        }}
                      >
                        <span className="flex-1 text-left">{t('nav.settings')}</span>
                        <ChevronDown className={cn('h-3 w-3 transition-transform', settingsSubOpen && 'rotate-180')} />
                      </button>
                      {settingsSubOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {filteredSettingsItems.map((item) => {
                            const isActive = location.pathname === item.to
                            return (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                className={cn(
                                  'block px-3 py-2 rounded-lg text-sm transition-colors',
                                  isActive ? 'font-medium' : 'hover:bg-white/10'
                                )}
                                style={{
                                  color: isActive ? accentColor : sidebarTextColor,
                                  backgroundColor: isActive ? secondaryColor : undefined
                                }}
                              >
                                {t(item.labelKey)}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
              </>
            )}
          </nav>

          {/* User Menu in Mobile */}
          {user && (
            <div className="absolute bottom-0 left-0 right-0 border-t bg-muted/50">
              <div className="flex items-center gap-2 px-3 py-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-sm">
                    {getUserInitials(user.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.role_name}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => { setChangePasswordOpen(true); setOpen(false); }}>
                      <KeyRound className="mr-2 h-4 w-4" />
                      {t('user.changePassword')}
                    </DropdownMenuItem>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>{t('user.language')}</span>
                        <span className="ml-auto text-xs text-muted-foreground">{currentLang?.flag}</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {supportedLanguages.map((lang) => (
                            <DropdownMenuItem
                              key={lang.code}
                              onClick={() => { changeLanguage(lang.code); setOpen(false); }}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <span>{lang.flag}</span>
                                <span>{lang.nativeName}</span>
                              </div>
                              {currentLanguage === lang.code && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Palette className="mr-2 h-4 w-4" />
                        <span>{t('user.theme')}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {theme === 'dark' ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem
                            onClick={() => { setTheme('light'); setOpen(false); }}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <Sun className="h-4 w-4" />
                              <span>{t('user.lightTheme')}</span>
                            </div>
                            {theme === 'light' && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setTheme('dark'); setOpen(false); }}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <Moon className="h-4 w-4" />
                              <span>{t('user.darkTheme')}</span>
                            </div>
                            {theme === 'dark' && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); setOpen(false); }} className="text-destructive focus:text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('user.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  )
}

// Protected route component that requires authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

// Onboarding guard component that redirects to onboarding if not completed
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const { isSuperAdmin } = useAuth()

  const { data: agencyConfig, isLoading } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    // Skip onboarding check for super admins
    if (isSuperAdmin) return

    // Redirect to onboarding if not completed
    if (!isLoading && agencyConfig && agencyConfig.onboarding_completed === false) {
      navigate('/onboarding', { replace: true })
    }
  }, [agencyConfig, isLoading, navigate, isSuperAdmin])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // If onboarding not completed and not super admin, don't render children (redirect will happen)
  if (!isSuperAdmin && agencyConfig && agencyConfig.onboarding_completed === false) {
    return null
  }

  return <>{children}</>
}

// Get user initials for avatar
function getUserInitials(fullName: string): string {
  const names = fullName.trim().split(' ')
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
  }
  return fullName.substring(0, 2).toUpperCase()
}

// Header component with agency logo and user menu
function Header() {
  const { t } = useTranslation()
  const { user, logout, isSuperAdmin } = useAuth()
  const { agencyName, textColor } = useAgencyTheme()
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage()
  const { theme, setTheme } = useTheme()
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  if (!user) return null

  const currentLang = supportedLanguages.find(l => l.code === currentLanguage)

  // For superadmin, show "Super Admin Portal" instead of agency name
  const displayName = isSuperAdmin ? t('nav.superAdminPortal') : agencyName

  return (
    <>
      <header
        className="hidden md:flex h-14 items-center justify-between px-5 sticky top-0 z-40 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border-b border-white/20 dark:border-white/10"
        style={{
          borderColor: `${textColor}10`,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Left Section - Page Title */}
        <div className="flex items-center gap-3">
          {isSuperAdmin ? (
            // Super Admin Portal - show shield icon and title
            <Link to="/super-admin/agencies" className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <span className="text-lg font-semibold" style={{ color: textColor }}>{displayName}</span>
            </Link>
          ) : (
            // Regular user - show page title
            <span className="text-lg font-semibold" style={{ color: textColor }}>Agency Overview</span>
          )}
        </div>

        {/* Center Section - Search Bar */}
        {!isSuperAdmin && (
          <div className="flex-1 max-w-md mx-8">
            <GlobalSearch textColor={textColor} />
          </div>
        )}

        {/* Right Section - Icons and User Menu */}
        <div className="flex items-center gap-3">
          {/* Notification Bell - only show for non-super-admin users */}
          {!isSuperAdmin && <NotificationBell />}

          {/* Help Button - only show for non-super-admin users */}
          {!isSuperAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-gray-100 h-9 w-9" title={t('nav.help', 'Help')}>
                  <HelpCircle className="h-5 w-5" style={{ color: textColor }} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{t('nav.helpAndSupport', 'Help & Support')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/support/user-guide" className="flex items-center cursor-pointer">
                    <Book className="mr-2 h-4 w-4" />
                    {t('support.userGuide', 'User Guide')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/support" className="flex items-center cursor-pointer">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t('nav.supportTickets', 'Support Tickets')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Menu with Name */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-auto py-1.5 px-2 hover:bg-gray-100">
                <span className="text-sm font-medium hidden sm:block" style={{ color: textColor }}>{user.full_name}</span>
                <Avatar className="h-9 w-9 border-2 border-gray-200">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getUserInitials(user.full_name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
	            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user.full_name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user.user_email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                <KeyRound className="mr-2 h-4 w-4" />
                {t('user.changePassword')}
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Globe className="mr-2 h-4 w-4" />
                  <span>{t('user.language')}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{currentLang?.flag}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {supportedLanguages.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.nativeName}</span>
                        </div>
                        {currentLanguage === lang.code && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Palette className="mr-2 h-4 w-4" />
                  <span>{t('user.theme')}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {theme === 'dark' ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => setTheme('light')}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Sun className="h-4 w-4" />
                        <span>{t('user.lightTheme')}</span>
                      </div>
                      {theme === 'light' && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setTheme('dark')}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4" />
                        <span>{t('user.darkTheme')}</span>
                      </div>
                      {theme === 'dark' && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                {t('user.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </>
  )
}

function AppContent() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const { hamburgerMenuColor } = useAgencyTheme()
  const { isSuperAdmin } = useAuth()

  return (
    <>
      <div
        className="min-h-screen flex"
        style={{
          background: `linear-gradient(135deg, ${hamburgerMenuColor} 0%, ${hamburgerMenuColor}DD 50%, ${hamburgerMenuColor}BB 100%)`,
        }}
      >
        {/* Desktop Sidebar - Full Height */}
        <div className="hidden md:flex flex-col h-screen sticky top-0">
          <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        </div>

        {/* Main Content Area with Header - Light grey panel with rounded corners */}
        <div className="flex-1 flex flex-col md:m-2 md:rounded-2xl bg-gray-100 dark:bg-neutral-900 overflow-hidden md:h-[calc(100vh-1rem)]">
          {/* Desktop Header */}
          <Header />

          {/* Mobile Header */}
          <MobileNav />

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto relative">
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/boards/:boardId" element={<BoardPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/new" element={<CreateClientPage />} />
              <Route path="/clients/:id" element={<CreateClientPage mode="view" />} />
              <Route path="/clients/:clientId/businesses" element={<BusinessListPage />} />
              <Route path="/clients/:clientId/businesses/new" element={<BusinessFormPage />} />
              <Route path="/clients/:clientId/businesses/:businessId" element={<BusinessFormPage mode="view" />} />
              <Route path="/clients/:clientId/businesses/:businessId/edit" element={<BusinessFormPage mode="edit" />} />
              <Route path="/expenses" element={<ExpensesPage />} />
              <Route path="/expenses/new" element={<ExpenseFormPage />} />
              <Route path="/expenses/:id/edit" element={<ExpenseFormPage />} />
              <Route path="/billing/quotes" element={<QuotesPage />} />
              <Route path="/billing/quotes/new" element={<CreateQuotePage />} />
              <Route path="/billing/quotes/:id" element={<CreateQuotePage mode="view" />} />
              <Route path="/billing/quotes/:id/edit" element={<CreateQuotePage mode="edit" />} />
              <Route path="/billing/quotes/:id/convert" element={<CreateQuotePage mode="convert" />} />
              <Route path="/billing/invoices" element={<InvoicesPage />} />
              <Route path="/billing/invoices/new" element={<CreateInvoicePage />} />
              <Route path="/billing/invoices/:id" element={<CreateInvoicePage mode="view" />} />
              <Route path="/billing/invoices/:id/edit" element={<CreateInvoicePage mode="edit" />} />
              <Route path="/billing/payments" element={<PaymentHistoryPage />} />
              <Route path="/billing/recurring-invoices" element={<RecurringInvoicesPage />} />
              <Route path="/billing/recurring-invoices/new" element={<CreateRecurringInvoicePage />} />
              <Route path="/billing/recurring-invoices/:id" element={<CreateRecurringInvoicePage mode="view" />} />
              <Route path="/billing/recurring-invoices/:id/edit" element={<CreateRecurringInvoicePage mode="edit" />} />
              <Route path="/masters/tax" element={<TaxMasterPage />} />
              <Route path="/masters/fees" element={<FeesMasterPage />} />
              <Route path="/masters/categories" element={<CategoryMasterPage />} />
              <Route path="/masters/products" element={<ProductMasterPage />} />
              <Route path="/masters/payment-methods" element={<PaymentMethodMasterPage />} />
              <Route path="/masters/exchange-rates" element={<ExchangeRatesPage />} />
              <Route path="/uam/roles" element={<RolesPage />} />
              <Route path="/uam/roles/new" element={<CreateRolePage />} />
              <Route path="/uam/roles/:id" element={<CreateRolePage mode="view" />} />
              <Route path="/uam/roles/:id/edit" element={<CreateRolePage mode="edit" />} />
              <Route path="/uam/users" element={<UsersPage />} />
              <Route path="/settings/agency" element={<AgencyConfigurationPage />} />
              <Route path="/settings/custom-fields" element={<CustomFieldsPage />} />
              <Route path="/settings/billing-templates" element={<InvoiceTemplatesPage />} />
              <Route path="/settings/billing-templates/new" element={<InvoiceTemplateFormPage />} />
              <Route path="/settings/billing-templates/:id" element={<InvoiceTemplateFormPage mode="view" />} />
              <Route path="/settings/billing-templates/:id/edit" element={<InvoiceTemplateFormPage mode="edit" />} />
              <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
              <Route path="/settings/invoice" element={<InvoiceSettingsPage />} />
              <Route path="/settings/payment-gateways" element={<PaymentGatewaySettingsPage />} />
              <Route path="/settings/payment-gateways/new" element={<PaymentGatewayFormPage />} />
              <Route path="/settings/payment-gateways/:id/edit" element={<PaymentGatewayFormPage mode="edit" />} />
              <Route path="/settings/esigning" element={<ESigningSettingsPage />} />
              <Route path="/settings/audit-logs" element={<AuditLogsPage />} />
              {/* CRM */}
              <Route path="/crm/dashboard" element={<CRMDashboardPage />} />
              <Route path="/crm/reports" element={<CRMReportsPage />} />
              <Route path="/crm/leads" element={<LeadsPage />} />
              <Route path="/crm/leads/new" element={<LeadFormPage />} />
              <Route path="/crm/leads/:id" element={<LeadFormPage mode="view" />} />
              <Route path="/crm/leads/:id/edit" element={<LeadFormPage mode="edit" />} />
              <Route path="/crm/settings" element={<LeadSettingsPage />} />
              <Route path="/crm/pipelines" element={<PipelineSettingsPage />} />
              <Route path="/crm/deals" element={<DealsKanbanPage />} />
              <Route path="/crm/deals/new" element={<DealFormPage />} />
              <Route path="/crm/deals/:dealId/edit" element={<DealFormPage />} />
              <Route path="/crm/deals/:dealId" element={<DealDetailPage />} />
              <Route path="/crm/automations" element={<AutomationsPage />} />
              <Route path="/crm/automations/new" element={<AutomationFormPage />} />
              <Route path="/crm/automations/logs" element={<AutomationLogsPage />} />
              <Route path="/crm/automations/:ruleId" element={<AutomationFormPage />} />
              {/* Contracts */}
              <Route path="/contracts" element={<ContractsPage />} />
              <Route path="/contracts/new" element={<ContractFormPage />} />
              <Route path="/contracts/templates" element={<ContractTemplatesPage />} />
              <Route path="/contracts/templates/new" element={<ContractTemplateFormPage />} />
              <Route path="/contracts/templates/:id" element={<ContractTemplateFormPage mode="view" />} />
              <Route path="/contracts/templates/:id/edit" element={<ContractTemplateFormPage mode="edit" />} />
              <Route path="/contracts/:id" element={<ContractDetailPage />} />
              <Route path="/contracts/:id/edit" element={<ContractFormPage mode="edit" />} />
              <Route path="/contracts/:id/send" element={<ContractSendPage />} />
              {/* Time Tracking */}
              <Route path="/time-tracking" element={<TimeTrackingPage />} />
              <Route path="/time-reports" element={<TimeReportsPage />} />
              {/* Social Media Calendar */}
              <Route path="/social-media/calendars" element={<SocialMediaCalendarsPage />} />
              <Route path="/social-media/calendars/:id" element={<SocialMediaCalendarDetailPage />} />
              {/* Reconciliation */}
              <Route path="/reconciliation/inbox" element={<TransactionInboxPage />} />
              <Route path="/reconciliation/bank-accounts" element={<BankAccountsPage />} />
              <Route path="/reconciliation/statements" element={<StatementsPage />} />
              <Route path="/reconciliation/rules" element={<ReconciliationRulesPage />} />
              <Route path="/reconciliation/rule-suggestions" element={<RuleSuggestionsPage />} />
              {/* Support Tickets */}
              <Route path="/support" element={<SupportTicketsPage />} />
              <Route path="/support/user-guide" element={<UserGuidePage />} />
              {/* Subscription */}
              <Route path="/subscription" element={<SubscriptionPage />} />
              {/* Super Admin Routes */}
              <Route path="/super-admin/agencies" element={<AgenciesPage />} />
              <Route path="/super-admin/countries" element={<CountriesPage />} />
              <Route path="/super-admin/currencies" element={<CurrenciesPage />} />
              <Route path="/super-admin/support" element={<AdminSupportTicketsPage />} />
              <Route path="/super-admin/scheduler" element={<SchedulerPage />} />
              <Route path="/super-admin/health" element={<HealthCheckPage />} />
              <Route path="/super-admin/logs" element={<LogViewerPage />} />
            </Routes>
          </main>
        </div>
      </div>
      <Toaster richColors position="top-right" />
      {/* Hide Chatbot for Super Admins */}
      {!isSuperAdmin && <Chatbot />}
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
          <AgencyThemeProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/sign/:token" element={<PublicSigningPage />} />
              <Route path="/pay/:token" element={<PublicPaymentPage />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <PermissionProvider>
                      <NotificationProvider>
                        <OnboardingGuard>
                          <AppContent />
                        </OnboardingGuard>
                      </NotificationProvider>
                    </PermissionProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AgencyThemeProvider>
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  )
}

export default App

